import { HTTPClient, propsToDate } from '@abaco/web-common/src/HTTP';
import { PagingParams } from '@abaco/web-common/src/Paging';
import { Lot, LotFertilization, LotIrrigation, LotSummary, Pua, PuaCreate, ConfigCrop, ConfigWater, RequestPlot, ConfigFertilization, ConfigProduct, ConfigSoilInfo, Interdiction, InterdictionCode, PuaStatus, Spreading, Anomalies, PrintType, PuaPlotGroup, ExpirationDate } from '../models/PuaModel';

export default class ExampleApi {
    private http: HTTPClient;
    public context = "FARM_CROP_PLAN_MAP";

    constructor(http: HTTPClient) {
        this.http = http;
    }

    public async getPua(pkCuaa: number, puaId: number): Promise<Pua> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}`;
        return (await this.http.get(url)).data
    }

    public async getPuaStatus(): Promise<PuaStatus[]> {
        const url = `/pua-api/api/v1/data/pua/status`;
        return (await this.http.get(url)).data
    }

    public async getPuaList(pkCuaa: number, campaign: number | null): Promise<Pua[]> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua${campaign ? '?campaign=' + campaign : ''}`;
        return (await this.http.get(url)).data
    }

    public async getAvailablePlotListOld(pkCuaa: number, puaId: number): Promise<Lot[]> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/plots/available`;
        return (await this.http.get(url)).data
    }

    public async getAvailablePlotList(pkCuaa: number, puaId: number): Promise<PuaPlotGroup[]> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/plots/groups/available`;
        return (await this.http.get(url)).data
    }

    public async deletePua(pkCuaa: number, puaId: number): Promise<Pua[]> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}`;
        return (await this.http.delete(url)).data
    }

    public async addPua(pkCuaa: number, pua: PuaCreate): Promise<Pua> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua`;
        const params = {
            ...pua
        }
        return (await this.http.post(url, params)).data
    }

    public async sendPua(pkCuaa: number, pua: Pua, code: string): Promise<Pua> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${pua.puaId}/status?code=${code}`;
        return (await this.http.put(url)).data
    }

    public async addPlot(pkCuaa: number, requestPlot: RequestPlot, isGroup = false): Promise<any> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${requestPlot.puaId}/plots${isGroup ? '/group' : ''}`;
        const params = {
            ...requestPlot
        }
        return (await this.http.post(url, params)).data
    }

    public async editPlot(pkCuaa: number, requestPlot: RequestPlot): Promise<any> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${requestPlot.puaId}/plots/${requestPlot.lotId}`;
        const params = {
            ...requestPlot
        }
        return (await this.http.put(url, params)).data
    }

    public async removePlot(pkCuaa: number, puaId: number, lotId: number): Promise<any> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/plots/${lotId}`;
        return (await this.http.delete(url)).data
    }

    public async getLotSummary(pkCuaa: number, puaId: number, lotId: number): Promise<LotSummary> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/plots/${lotId}/summary`;
        return (await this.http.get(url)).data
    }

    public async getIrrigations(pkCuaa: number, puaId: number): Promise<LotIrrigation[]> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/irrigations`;
        return (await this.http.get(url)).data
    }

    public async addIrrigation(pkCuaa: number, puaId: number, irrigation: LotIrrigation): Promise<Pua> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/irrigations`;
        return (await this.http.post(url, irrigation)).data
    }

    public async deleteIrrigation(pkCuaa: number, puaId: number, operationId: number): Promise<any> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/irrigations/${operationId}`;
        return (await this.http.delete(url)).data
    }

    public async getFertilizations(pkCuaa: number, puaId: number): Promise<LotFertilization> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/fertilizations`;
        const fertilizations = (await this.http.get(url)).data;
        ["chemicals", "manures", "otherOrganics", "slurries"].forEach(fertType => fertilizations[fertType].forEach((fert: any) => propsToDate(fert, ["executionDate"])));
        return fertilizations;
    }

    public async addFertilization(pkCuaa: number, puaId: number, fertilization: LotFertilization): Promise<Pua> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/fertilizations`;
        return (await this.http.post(url, fertilization)).data
    }

    public async updateFertilization(pkCuaa: number, puaId: number, operationId: number, fertilization: LotFertilization): Promise<Pua> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/fertilizations/${operationId}`;
        return (await this.http.put(url, fertilization)).data
    }

    public async deleteFertilization(pkCuaa: number, puaId: number, operationId: number): Promise<any> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/fertilizations/${operationId}`;
        return (await this.http.delete(url)).data
    }

    public async updateIrrigations(pkCuaa: number, puaId: number, lotId: number, irrigations: LotIrrigation[]): Promise<Pua> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/plots/${lotId}/irrigations`;
        return (await this.http.post(url, irrigations)).data
    }

    public async updateFertilizations(pkCuaa: number, puaId: number, lotId: number, isDelete = false, fertilizations: LotFertilization): Promise<Pua> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/plots/${lotId}/fertilizations${isDelete ? '?delete=true' : ''}`;
        return (await this.http.post(url, fertilizations)).data
    }

    public async getFertilizers(pkCuaa: number): Promise<ConfigFertilization> {
        const url = `/pua-api/api/v1/data/fertilizers?farmId=${pkCuaa}`;
        return (await this.http.get(url)).data
    }

    public async getFertilizersFromCatalog(pkCuaa: number, paging: PagingParams, filter: string, fertilizerType: string): Promise<any> {
        const url = `/pua-api/api/v1/data/fertilizers/catalog?farmId=${pkCuaa}&limit=${paging.count}&offset=${paging.offset}&searchName=${filter}&fertilizerType=${fertilizerType}`;
        return (await this.http.get(url)).data
    }

    public async getWaterList(pkCuaa: number): Promise<ConfigWater[]> {
        const url = `/pua-api/api/v1/data/waters?farmId=${pkCuaa}`;
        return (await this.http.get(url)).data
    }

    public async getCropList(): Promise<ConfigCrop[]> {
        const url = `/pua-api/api/v1/data/crops`;
        return (await this.http.get(url)).data
    }

    public async getSoilInfoList(): Promise<ConfigSoilInfo[]> {
        const url = `/pua-api/api/v1/data/soilInfo`;
        return (await this.http.get(url)).data
    }

    public async getProductList(): Promise<ConfigProduct[]> {
        const url = `/pua-api/api/v1/data/products`;
        return (await this.http.get(url)).data
    }

    public async getNitrogenEfficiency(): Promise<any> {
        const url = `/pua-api/api/v1/data/nitrogenEfficiency`;
        return (await this.http.get(url)).data
    }

    public async getInterdictionsCodesList(): Promise<InterdictionCode[]> {
        const url = `/pua-api/api/v1/data/interdictions/codes`;
        return (await this.http.get(url)).data
    }

    public async getInterdictions(pkCuaa: number, puaId: number, campaign?: number): Promise<any[]> {
        const url = `/pua-api/api/v1/data/lawinterdictions${campaign ? '?campaign=' + campaign : ''}`;
        return (await this.http.get(url)).data
    }

    public async getInterdictionsList(pkCuaa: number, puaId: number): Promise<Interdiction[]> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/interdictions`;
        return (await this.http.get(url)).data
    }

    public async updateInterdictionsList(pkCuaa: number, puaId: number, interdictions: Interdiction[]): Promise<Interdiction[]> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/interdictions`;
        return (await this.http.post(url, interdictions)).data
    }

    public async getSpreadingsList(pkCuaa: number, puaId: number): Promise<Spreading[]> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/spreadings`;
        return (await this.http.get(url)).data
    }

    public async updateSpreadingsList(pkCuaa: number, puaId: number, spreadings: Spreading[]): Promise<Spreading[]> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/spreadings`;
        return (await this.http.post(url, spreadings)).data
    }

    public async getAnomalies(pkCuaa: number, puaId: number): Promise<Anomalies[]> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/anomalies`;
        return (await this.http.get(url)).data
    }

    public async getPrintTypes(): Promise<PrintType[]> {
        const url = `/pua-api/api/v1/data/prints/types`;
        return (await this.http.get(url)).data
    }

    public async generatePrint(pkCuaa: number, puaId: number, printId: number): Promise<any> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/prints/${printId}`;
        return (await this.http.post(url)).data
    }

    public async printStatus(pkCuaa: number, puaId: number, printId: number): Promise<any> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/prints/${printId}/status`;
        return (await this.http.get(url)).data
    }

    public async downloadPrint(url: string): Promise<any> {
        return (await this.http.get(url)).data
    }

    public async getLastPrints(pkCuaa: number, puaId: number): Promise<any[]> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/prints`;
        return (await this.http.get(url)).data
    }

    public async getPuaPrint(pkCuaa: number, puaId: number, printId: number): Promise<{ url: string }> {
        const url = `/pua-api/api/v1/farm/${pkCuaa}/pua/${puaId}/prints/${printId}/url`;
        return (await this.http.get(url)).data
    }

    public async getSubmissionDeadline(campaign: number): Promise<ExpirationDate> {
        const url = `/pua-api/api/v1/config/pua_submission_deadline?campaign=${campaign}`;
        return (await this.http.get(url)).data
    }
}
